/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Ember from 'ember';
import Component from '@ember/component';
import { assert } from '@ember/debug';
import config from 'cio/config/environment';
import { task, timeout } from 'ember-concurrency';
import { VWB_ACTION_STATE_CHANGED } from 'cio/services/event-bus';
import { tagName } from '@ember-decorators/component';

const RELOAD_INTERVAL = config.APP.actionReloadInterval;

/*
  Basic Usage:

  ```handlebars
  {{visual-workflow-builder-v2/node/workflow-action/delete-progress
    action=Action
    subjects=subjects
    onDestroyed=(action "removeAction" action)}}
  ```
 */
@tagName('')
export default class DeleteProgress extends Component {
  @service apiErrors;
  @service eventBus;
  @service visualWorkflowBuilder;

  action = null;
  onDestroyed() {}

  @computed('action.{isDeleting,isEvaluating}')
  // eslint-disable-next-line getter-return
  get verb() {
    let action = this.get('action');

    if (action.get('isDeleting')) {
      return 'delete';
    } else if (action.get('isEvaluating')) {
      return 'update';
    }

    return;
  }

  reloadAction = task(
    {
      drop: true,
    },
    async () => {
      if (Ember.testing) {
        return;
      }

      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          await this.action.reload();
        } catch (response) {
          let status = response.errors?.[0]?.status;

          if (status === '404') {
            return this.onDestroyed();
          }
        }

        if (!Ember.testing) {
          await timeout(RELOAD_INTERVAL);
        }
      }
    },
  );

  init() {
    super.init(...arguments);

    assert('[component:cio-action-progress] "action" is required', this.get('action'));
    this.get('reloadAction').perform();
  }

  didInsertElement() {
    super.didInsertElement(...arguments);

    // Rendering this component shifts DOM items because it adds a new block
    // within the workflow item. This means that the VWB paths are no longer
    // pointing to the right spots, so we need to redraw them.
    this.visualWorkflowBuilder.redrawConnectingLines();
    this.eventBus.publish(VWB_ACTION_STATE_CHANGED, this.action);
  }

  didDestroyElement() {
    super.didDestroyElement(...arguments);

    // Same as when rendering, destroying this component shifts the workflow
    // again, so we have to redraw the paths.
    this.visualWorkflowBuilder.redrawConnectingLines();
    this.eventBus.publish(VWB_ACTION_STATE_CHANGED, this.action);
  }
}
